/*
 * UlanziRevealOnScroll
 * 基于 IntersectionObserver 的 section 通用入场动画工具。
 *
 * 用法（脚本已加载）：
 * window.UlanziRevealOnScroll.enqueueInit({
 *   options: {
 *     root: HTMLElement, // 必填：监听范围的根节点（通常是当前 section 根元素）
 *     selector: '.my-reveal-item', // 必填：root 内需要做入场动画的元素选择器
 *     visibleClass: 'is-visible', // 可选：元素进入视口后追加的 class（用于触发 CSS 过渡）
 *     observedAttr: 'data-my-observed', // 可选：标记“已加入 observer”的属性名，避免重复 observe
 *     threshold: 0.1, // 可选：元素可见比例达到多少才触发（0.1 = 10%）
 *     rootMargin: '0px 0px -40px 0px', // 可选：触发边界偏移（负 bottom 表示更早触发）
 *     unobserveOnVisible: true // 可选：触发后是否取消监听（true 只播一次）
 *   },
 *   onReady: function (controller) {
 *     // 新增 DOM 后可调用 controller.observePending() 继续监听
 *     // 不再需要时可调用 controller.disconnect() 断开监听
 *   },
 *   onFallback: function () {
 *     // 参数无效导致初始化失败时触发
 *   }
 * });
 *
 * 用法（脚本可能尚未加载）：
 * window.UlanziRevealOnScrollQueue = window.UlanziRevealOnScrollQueue || [];
 * window.UlanziRevealOnScrollQueue.push({ options: { root: el, selector: '.item' } });
 */
(function () {
  if (window.UlanziRevealOnScroll) return;

  const queueKey = 'UlanziRevealOnScrollQueue';

  // 使用共享 API 实例执行单个队列初始化任务。
  const runInitJob = function (api, job) {
    if (!job || !job.options) return;

    const controller = api.init(job.options);
    if (controller) {
      if (typeof job.onReady === 'function') {
        job.onReady(controller);
      }
      return;
    }

    if (typeof job.onFallback === 'function') {
      job.onFallback();
    }
  };

  window.UlanziRevealOnScroll = {
    /*
     * options 参数结构：
     * {
     *   root: HTMLElement,                 // 必填：当前这组 reveal 元素的查询根节点
     *   selector: string,                  // 必填：用于 querySelectorAll 的目标元素选择器
     *   visibleClass?: string,             // 可选：元素可见后添加的 class，默认 is-visible
     *   observedAttr?: string,             // 可选：已监听标记属性，默认 data-reveal-observed
     *   threshold?: number,                // 可选：IntersectionObserver threshold，默认 0.1
     *   rootMargin?: string,               // 可选：IntersectionObserver rootMargin，默认 0px 0px -40px 0px
     *   unobserveOnVisible?: boolean       // 可选：可见后是否 unobserve，默认 true
     * }
     *
     * 返回值：
     * - 初始化成功：controller { observePending(), disconnect() }
     * - 参数不合法：null
     */
    init: function (options) {
      const root = options && options.root;
      if (!root) return null;

      const selector = options && typeof options.selector === 'string' ? options.selector.trim() : '';
      if (!selector) {
        return null;
      }

      const visibleClass = options.visibleClass || 'is-visible';
      const observedAttr = options.observedAttr || 'data-reveal-observed';
      const threshold = typeof options.threshold === 'number' ? options.threshold : 0.1;
      const rootMargin = options.rootMargin || '0px 0px -40px 0px';
      const unobserveOnVisible = options.unobserveOnVisible !== false;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const observePending = function (observer) {
        root.querySelectorAll(selector).forEach(function (el) {
          if (!el.classList.contains(visibleClass) && !el.hasAttribute(observedAttr)) {
            el.setAttribute(observedAttr, 'true');
            observer.observe(el);
          }
        });
      };

      if (reduceMotion || typeof window.IntersectionObserver === 'undefined') {
        root.querySelectorAll(selector).forEach(function (el) {
          el.classList.add(visibleClass);
        });
        return {
          observePending: function () {}
        };
      }

      const observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add(visibleClass);
              if (unobserveOnVisible) observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: threshold,
          rootMargin: rootMargin
        }
      );

      observePending(observer);

      return {
        observePending: function () {
          observePending(observer);
        },
        disconnect: function () {
          observer.disconnect();
        }
      };
    },

    /*
     * job 参数结构：
     * {
     *   options: <与 init 一致的 options>, // 必填：初始化 reveal 所需配置
     *   onReady?: function (controller),   // 可选：初始化成功回调
     *   onFallback?: function ()           // 可选：初始化失败回调（参数无效等场景）
     * }
     */
    enqueueInit: function (job) {
      runInitJob(window.UlanziRevealOnScroll, job);
    }
  };

  // 如果脚本执行前已有排队任务，这里立即按顺序执行。
  const queue = window[queueKey];
  if (Array.isArray(queue) && queue.length) {
    queue.splice(0).forEach(function (job) {
      window.UlanziRevealOnScroll.enqueueInit(job);
    });
  }
})();
